import React from 'react';
import styled from 'styled-components';

import {
  ResponseFormStatus,
  ResponseResult,
  ResponseTone,
  SupportAction,
  SupportActionIcon,
  SupportTicketFormResult
} from '../../../types';
import HighlightedText from '../../HighlightedText';
import { ResponseActions, ResponseIcon } from '../../ResponseActionButton';
import {
  RESPONSE_TONE,
  responseTone,
  responseType,
  rsColor,
  rsMono,
  rsv
} from '../../responseScale';

export type ResponseResultStatus = Exclude<ResponseFormStatus, 'draft'>;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

/**
 * 6459:17509: `Apr 4, 2026 11:48 AM`, the wall clock of the ISO string's own offset, so the
 * browser's timezone never shifts it. Anything else is shown as sent.
 */
export const formatResultTimestamp = (value: string): string => {
  const match = ISO_DATE_TIME.exec(value);
  if (match === null) {
    return value;
  }
  const [, year, month, day, hour, minute] = match;
  const hours = Number(hour);
  const meridiem = hours < 12 ? 'AM' : 'PM';
  return `${MONTHS[Number(month) - 1]} ${Number(day)}, ${year} ${
    hours % 12 || 12
  }:${minute} ${meridiem}`;
};

/** `message` as lines, with every `highlights` run drawn bold. */
const ResultMessage: React.FC<{ result: ResponseResult; className?: string }> = ({
  result,
  className
}) =>
  result.message ? (
    <p className={className}>
      <HighlightedText text={result.message} highlights={result.highlights} />
    </p>
  ) : null;

interface IResultLook {
  icon: SupportActionIcon;
  ink: string;
  fill: string;
  ring: string;
  glyph: string;
}

// 6459:16528 / 17500 / 16494. The status picks the look; every word is the payload's.
const TICKET_RESULT_LOOK: Record<ResponseResultStatus, IResultLook> = {
  processing: {
    icon: 'sync',
    ink: rsColor.primary,
    fill: rsColor.primary,
    ring: rsColor.panel,
    glyph: rsColor.white
  },
  success: {
    icon: 'check',
    ink: RESPONSE_TONE.success.ink,
    fill: RESPONSE_TONE.success.fill,
    ring: RESPONSE_TONE.success.ink,
    glyph: RESPONSE_TONE.success.ink
  },
  error: {
    icon: 'history',
    ink: RESPONSE_TONE.error.ink,
    fill: RESPONSE_TONE.error.ink,
    ring: rsColor.panel,
    glyph: rsColor.white
  }
};

// 6459:16475: a borderless card under the panel, its content centred.
const ResultCard = styled.section`
  && {
    margin-top: ${rsv.space('resultTop')}px;
  }

  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rsv.space('resultBox')};
  border-radius: ${rsv.radius('panel')}px;
  background: ${rsColor.panel};
  text-align: center;
`;

const ResultCircle = styled.span<{ $look: IResultLook }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${rsv.size('resultIcon')}px;
  height: ${rsv.size('resultIcon')}px;
  border: 2px solid ${({ $look }) => $look.ring};
  border-radius: 50%;
  background: ${({ $look }) => $look.fill};
  color: ${({ $look }) => $look.glyph};

  .anticon {
    font-size: ${rsv.size('resultGlyph')}px;
  }
`;

const ResultTitle = styled.h5<{ $ink: string }>`
  margin: ${rsv.space('resultGap')}px 0 0;
  color: ${({ $ink }) => $ink};
  white-space: pre-line;
  ${responseType('resultTitle')}
`;

const ResultMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${rsv.space('resultMetaGap')}px;
  color: ${rsColor.secondary};
  ${responseType('resultLabel')}

  > * + * {
    margin-left: ${rsv.space('chipGap')}px;
  }
`;

const CardMessage = styled(ResultMessage)`
  margin: ${rsv.space('resultMetaGap')}px 0 0;
  color: ${rsColor.secondary};
  white-space: pre-line;
  ${responseType('cell')}
`;

// 6459:17506: IBM Plex Mono Bold 12 on the link hue at 14%.
const TicketChip = styled.span`
  padding: ${rsv.space('chip')};
  border-radius: ${rsv.radius('chip')}px;
  background: ${RESPONSE_TONE.info.fill};
  color: ${RESPONSE_TONE.info.ink};
  font-family: ${rsMono};
  ${responseType('statusLine', 700)}
`;

const Caption = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${rsv.space('resultMetaGap')}px 0 0;
  color: ${rsColor.secondary};
  ${responseType('caption')}

  .anticon {
    font-size: ${rsv.size('captionIcon')}px;
  }

  > * + * {
    margin-left: ${rsv.space('captionIconGap')}px;
  }
`;

const CentredActions = styled(ResponseActions)`
  justify-content: center;
`;

interface IResultProps<TResult extends ResponseResult> {
  status: ResponseResultStatus;
  result: TResult;
  onAction: (action: SupportAction) => void;
}

/** 6459:16869 / 17172 / 16542: the ticket form's processing, success and error card. */
export const TicketResultCard: React.FC<IResultProps<SupportTicketFormResult>> = ({
  status,
  result,
  onAction
}) => {
  const look = TICKET_RESULT_LOOK[status];
  return (
    <ResultCard role="status" data-testid="orbit-response-result" data-status={status}>
      <ResultCircle $look={look}>
        <ResponseIcon icon={look.icon} />
      </ResultCircle>
      <ResultTitle $ink={look.ink}>{result.title}</ResultTitle>
      <CardMessage result={result} />
      {result.ticket_id ? (
        <ResultMeta>
          <span>{result.ticket_id.label}</span>
          <TicketChip>{result.ticket_id.value}</TicketChip>
        </ResultMeta>
      ) : null}
      {result.status ? (
        <ResultMeta>
          <span>{result.status.label}</span>
          <strong style={{ color: responseTone(result.status.tone).ink }}>
            {result.status.value}
          </strong>
        </ResultMeta>
      ) : null}
      {result.submitted_at ? (
        <Caption>
          <ResponseIcon icon="clock" />
          <span>
            {result.submitted_at.label} {formatResultTimestamp(result.submitted_at.value)}
          </span>
        </Caption>
      ) : null}
      <CentredActions actions={result.actions} onAction={onAction} />
    </ResultCard>
  );
};

const BANNER_LOOK: Record<ResponseResultStatus, { tone: ResponseTone; icon: SupportActionIcon }> = {
  processing: { tone: 'info', icon: 'sync' },
  success: { tone: 'success', icon: 'check' },
  error: { tone: 'error', icon: 'history' }
};

// 6265:4611: below and outside the panel, tinted and ruled in the status tone.
const Banner = styled.section<{ $ink: string; $fill: string }>`
  && {
    margin-top: ${rsv.space('bannerTop')}px;
  }

  display: flex;
  align-items: flex-start;
  padding: ${rsv.space('banner')};
  border: 1px solid ${({ $ink }) => $ink};
  border-radius: ${rsv.radius('panel')}px;
  background: ${({ $fill }) => $fill};

  > * + * {
    margin-left: ${rsv.space('bannerGap')}px;
  }
`;

// 6265:4612: a 32 disc in the tone with a 17 glyph in the page ink.
const BannerTick = styled.span<{ $ink: string }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: ${rsv.size('bannerTick')}px;
  height: ${rsv.size('bannerTick')}px;
  border-radius: 50%;
  background: ${({ $ink }) => $ink};
  color: ${rsColor.page};

  .anticon {
    font-size: ${rsv.size('bannerTickGlyph')}px;
  }
`;

const BannerText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const BannerTitle = styled.h5<{ $ink: string }>`
  margin: 0;
  color: ${({ $ink }) => $ink};
  ${responseType('bannerTitle')}
`;

const BannerMessage = styled(ResultMessage)`
  margin: ${rsv.space('bannerTextGap')}px 0 0;
  color: ${rsColor.secondary};
  white-space: pre-line;
  ${responseType('bannerBody')}
`;

/** 6423:10690: the renewal request's result, a banner in the status tone. */
export const ResultBanner: React.FC<IResultProps<ResponseResult>> = ({
  status,
  result,
  onAction
}) => {
  const look = BANNER_LOOK[status];
  const { ink, fill } = responseTone(look.tone);
  return (
    <Banner
      $ink={ink}
      $fill={fill}
      role="status"
      data-testid="orbit-response-result"
      data-status={status}
    >
      <BannerTick $ink={ink}>
        <ResponseIcon icon={look.icon} />
      </BannerTick>
      <BannerText>
        <BannerTitle $ink={ink}>{result.title}</BannerTitle>
        <BannerMessage result={result} />
        <ResponseActions actions={result.actions} onAction={onAction} />
      </BannerText>
    </Banner>
  );
};
